import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { langClass } from "@/i18n/langUtils";
import { useT } from "@/i18n/LanguageContext";
import SectionHeading from "@/components/SectionHeading";
import api from "@/lib/api";
import { asArray } from "@/lib/safeData";
import { localizeArticles, localizeCategory } from "@/i18n/localizeArticle";
import { LOSS_CATEGORY_KEYS } from "@/data/lossCategoryGuides";
import {
  BookOpen, Clock, ChevronRight, FileText, ShieldAlert,
  CheckCircle2, AlertTriangle, Scale, Lightbulb,
} from "lucide-react";

const Education = () => {
  const { t, lang } = useT();
  const edu = t.education || {};
  const lc = t.lossCategories || {};
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("all");

  useEffect(() => {
    api.get("/articles").then((r) => setItems(asArray(r.data))).catch(() => setItems([]));
  }, []);

  const excludeCategories = useMemo(
    () => new Set(asArray(edu.excludeCategories).length ? edu.excludeCategories : ["Real Stories"]),
    [edu.excludeCategories]
  );

  const articleList = useMemo(() => {
    const localized = localizeArticles(items, lang);
    return localized.filter((a) => !excludeCategories.has(a.category));
  }, [items, lang, excludeCategories]);

  const categories = useMemo(() => {
    const keys = Array.from(new Set(articleList.map((a) => a.categoryKey).filter(Boolean)));
    return ["all", ...keys];
  }, [articleList]);

  const visible = useMemo(() => {
    if (active === "all") return articleList;
    return articleList.filter((a) => a.categoryKey === active);
  }, [articleList, active]);

  const langCls = langClass(lang);

  const categoryLabel = (c) => (c === "all" ? edu.filterAll || "All" : localizeCategory(c, lang));

  const filingGuides = useMemo(() => {
    const itemsByKey = lc.items || [];
    return LOSS_CATEGORY_KEYS.map((key) => {
      const meta = itemsByKey.find((i) => i.key === key);
      return { key, title: meta?.title || key, authority: meta?.authority || "" };
    });
  }, [lc.items]);

  useEffect(() => {
    setActive("all");
  }, [lang]);

  const topics = asArray(edu.understandingTopics);
  const preventionItems = asArray(edu.preventionItems);
  const chooseChecks = asArray(edu.chooseServiceChecks);
  const fightSteps = asArray(edu.fightSteps);

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-8 py-16 md:py-24" data-testid="education-page">
      <SectionHeading eyebrow={edu.eyebrow} title={edu.title} subtitle={edu.subtitle} />

      {/* How losses happen */}
      {topics.length > 0 && (
        <section className="mt-14" data-testid="edu-understanding">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-5 w-5 text-[#FF3B30]" />
            <h2 className={`font-display text-2xl font-bold ${langCls}`}>{edu.understandingTitle}</h2>
          </div>
          <p className={`text-sm text-white/60 max-w-3xl mb-8 ${langCls}`}>{edu.understandingDesc}</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {topics.map((topic, i) => (
              <div key={i} className="surface-card p-6" data-testid={`edu-topic-${i}`}>
                <div className={`font-display text-lg font-bold text-white ${langCls}`}>{topic.title}</div>
                <p className={`mt-2 text-sm text-white/65 leading-relaxed ${langCls}`}>{topic.desc}</p>
                {asArray(topic.points).length > 0 && (
                  <ul className="mt-4 space-y-2">
                    {topic.points.map((pt, j) => (
                      <li key={j} className={`flex items-start gap-2 text-sm text-white/70 ${langCls}`}>
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#D4AF37] flex-shrink-0" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Prevention */}
      {preventionItems.length > 0 && (
        <section className="mt-14 surface-card p-7 md:p-10" data-testid="edu-prevention">
          <div className="flex items-center gap-2 mb-6">
            <ShieldAlert className="h-5 w-5 text-[#D4AF37]" />
            <h2 className={`font-display text-2xl font-bold ${langCls}`}>{edu.preventionTitle}</h2>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {preventionItems.map((item, i) => (
              <li key={i} className={`flex items-start gap-3 text-sm text-white/75 leading-relaxed ${langCls}`}>
                <CheckCircle2 className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Choosing services */}
      {chooseChecks.length > 0 && (
        <section className="mt-14" data-testid="edu-choose-service">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="h-5 w-5 text-[#D4AF37]" />
            <h2 className={`font-display text-2xl font-bold ${langCls}`}>{edu.chooseServiceTitle}</h2>
          </div>
          <p className={`text-sm text-white/60 max-w-3xl mb-8 ${langCls}`}>{edu.chooseServiceIntro}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {chooseChecks.map((check, i) => (
              <div key={i} className="surface-card p-5" data-testid={`edu-choose-${i}`}>
                <div className={`font-display font-bold text-white ${langCls}`}>{check.title}</div>
                <p className={`mt-2 text-sm text-white/65 leading-relaxed ${langCls}`}>{check.desc}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* How to fight */}
      {fightSteps.length > 0 && (
        <section className="mt-14" data-testid="edu-fight">
          <div className="flex items-center gap-2 mb-2">
            <Scale className="h-5 w-5 text-[#D4AF37]" />
            <h2 className={`font-display text-2xl font-bold ${langCls}`}>{edu.fightTitle}</h2>
          </div>
          <p className={`text-sm text-white/60 max-w-3xl mb-8 ${langCls}`}>{edu.fightIntro}</p>
          <div className="space-y-4">
            {fightSteps.map((s, i) => (
              <div key={i} className="surface-card p-5 flex gap-4 items-start" data-testid={`edu-fight-step-${i}`}>
                <div className="font-mono-alt text-xs text-[#D4AF37] pt-1">{s.step}</div>
                <div>
                  <div className={`font-display font-bold text-white ${langCls}`}>{s.title}</div>
                  <p className={`mt-1 text-sm text-white/65 leading-relaxed ${langCls}`}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Filing guides — secondary, compact */}
      <section className="mt-16 pt-14 border-t border-white/10" data-testid="filing-guides">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-5 w-5 text-[#D4AF37]" />
          <h2 className={`font-display text-xl font-bold ${langCls}`}>{edu.filingGuidesTitle}</h2>
        </div>
        <p className={`text-sm text-white/55 mb-6 max-w-3xl ${langCls}`}>
          {edu.filingGuidesDesc}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filingGuides.map(({ key, title, authority }) => (
            <Link
              key={key}
              to={`/loss/${key}`}
              className="surface-card p-4 group flex items-center justify-between gap-3 hover:border-[#D4AF37]/30 transition-colors"
              data-testid={`filing-guide-${key}`}
            >
              <div className="min-w-0">
                <div className={`font-display text-sm font-bold text-white group-hover:text-[#D4AF37] transition-colors ${langCls}`}>{title}</div>
                <div className="mt-0.5 text-[10px] text-[#D4AF37]/80 font-mono-alt truncate">{authority}</div>
              </div>
              <ChevronRight className="h-4 w-4 text-white/40 group-hover:text-[#D4AF37] flex-shrink-0" />
            </Link>
          ))}
        </div>
      </section>

      {/* Reference articles — no stories */}
      {articleList.length > 0 && (
        <section className="mt-16 pt-14 border-t border-white/10" data-testid="edu-articles">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="h-5 w-5 text-[#D4AF37]" />
            <h2 className={`font-display text-xl font-bold ${langCls}`}>{edu.articlesTitle || "Reference guides"}</h2>
          </div>
          <p className={`text-sm text-white/55 mb-6 ${langCls}`}>{edu.articlesDesc}</p>

          <div className="-mx-4 px-4 overflow-x-auto pb-2 md:mx-0 md:px-0" data-testid="edu-filters">
            <div className="flex flex-nowrap gap-2 min-w-min">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setActive(c)}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-mono-alt uppercase tracking-widest border transition-colors whitespace-nowrap ${
                    active === c ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/15 text-white/70 hover:border-white/30"
                  }`}
                  data-testid={`edu-filter-${c}`}
                >
                  {categoryLabel(c)}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="edu-grid">
            {visible.map((a) => (
              <article key={a.id} className="surface-card p-6 flex flex-col" data-testid={`article-${a.id}`}>
                <div className="inline-flex items-center gap-2 text-xs text-[#D4AF37] font-mono-alt uppercase tracking-widest">
                  <BookOpen className="h-3.5 w-3.5" /> {a.category}
                </div>
                <h3 className={`mt-3 font-display text-lg font-bold leading-snug ${langCls}`}>{a.title}</h3>
                <p className={`mt-2 text-sm text-white/65 leading-relaxed flex-1 ${langCls}`}>{a.excerpt}</p>
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between text-xs text-white/45 font-mono-alt">
                  <span className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3" /> {a.read_minutes} {edu.readMinutes}</span>
                  <span>{a.date}</span>
                </div>
              </article>
            ))}
          </div>

          {visible.length === 0 && (
            <div className={`surface-card p-10 text-center text-white/55 mt-8 ${langCls}`}>
              {edu.emptyCategory}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default Education;
